import { useState, useEffect, useCallback, createContext, useContext, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useNavigate, useParams, Navigate } from "react-router-dom";
import axios from "axios";
import html2canvas from "html2canvas";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import { Toaster } from "@/components/ui/toaster";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configure axios to send credentials
axios.defaults.withCredentials = true;

// ============ Auth Context ============
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [usage, setUsage] = useState(null);

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('glamwig_token');
      if (token) {
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(response.data);
        await fetchUsage(token);
      }
    } catch (error) {
      localStorage.removeItem('glamwig_token');
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsage = async (token) => {
    try {
      const response = await axios.get(`${API}/auth/usage`, {
        headers: { Authorization: `Bearer ${token || localStorage.getItem('glamwig_token')}` }
      });
      setUsage(response.data);
    } catch (error) {
      console.error("Error fetching usage:", error);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { user: userData, access_token } = response.data;
    localStorage.setItem('glamwig_token', access_token);
    setUser(userData);
    await fetchUsage(access_token);
    return userData;
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name });
    const { user: userData, access_token } = response.data;
    localStorage.setItem('glamwig_token', access_token);
    setUser(userData);
    await fetchUsage(access_token);
    return userData;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (error) {
      console.error("Logout error:", error);
    }
    localStorage.removeItem('glamwig_token');
    setUser(null);
    setUsage(null);
  };

  const refreshUsage = () => fetchUsage();

  return (
    <AuthContext.Provider value={{ user, loading, usage, login, register, logout, refreshUsage }}>
      {children}
    </AuthContext.Provider>
  );
};

// ============ Protected Route ============
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <div className="loading-spinner">Loading...</div>;
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Generate session ID
const getSessionId = () => {
  let sessionId = localStorage.getItem('glamwig_session');
  if (!sessionId) {
    sessionId = 'session_' + Math.random().toString(36).substr(2, 9);
    localStorage.setItem('glamwig_session', sessionId);
  }
  return sessionId;
};

// ============ Icons ============
const ShoppingBagIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z"/><path d="M3 6h18"/><path d="M16 10a4 4 0 0 1-8 0"/>
  </svg>
);

const UploadIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" x2="12" y1="3" y2="15"/>
  </svg>
);

const SparklesIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/><path d="M5 3v4"/><path d="M19 17v4"/><path d="M3 5h4"/><path d="M17 19h4"/>
  </svg>
);

const HeartIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/>
  </svg>
);

const DownloadIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" x2="12" y1="15" y2="3"/>
  </svg>
);

const ShareIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" x2="15.42" y1="13.51" y2="17.49"/><line x1="15.41" x2="8.59" y1="6.51" y2="10.49"/>
  </svg>
);

const MailIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
  </svg>
);

const TrashIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/>
  </svg>
);

const CheckIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

const UserIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
  </svg>
);

const LogOutIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" x2="9" y1="12" y2="12"/>
  </svg>
);

const CrownIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m2 4 3 12h14l3-12-6 7-4-7-4 7-6-7zm3 16h14"/>
  </svg>
);

const SearchIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/>
  </svg>
);

const XIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 6 6 18"/><path d="m6 6 12 12"/>
  </svg>
);

const HeartFilledIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/>
  </svg>
);

const FacebookIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

const InstagramIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
  </svg>
);

const PinterestIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0c-6.627 0-12 5.372-12 12 0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738.098.119.112.224.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.631-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146 1.124.347 2.317.535 3.554.535 6.627 0 12-5.373 12-12 0-6.628-5.373-12-12-12z"/>
  </svg>
);

// ============ Header Component ============
const Header = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <header className="glamwig-header">
      <div className="header-container">
        <Link to="/" className="logo">
          <span className="logo-icon">💜</span>
          <span className="logo-text">GlamWig</span>
        </Link>
        <nav className="nav-links">
          <Link to="/" className="nav-link">Home</Link>
          <Link to="/try-on" className="nav-link">Try On</Link>
          {user && (
            <Link to="/history" className="nav-link" data-testid="history-link">
              <HeartIcon /> My Results
            </Link>
          )}
          {user ? (
            <div className="user-menu">
              <span className="user-email">{user.email}</span>
              {user.is_subscribed && <Badge className="pro-badge"><CrownIcon /> Pro</Badge>}
              <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-btn">
                <LogOutIcon />
              </Button>
            </div>
          ) : (
            <Link to="/login" className="nav-link" data-testid="login-link">
              <UserIcon /> Sign In
            </Link>
          )}
        </nav>
      </div>
    </header>
  );
};

// ============ Footer Component ============
const Footer = () => {
  return (
    <footer className="glamwig-footer">
      <div className="footer-container">
        <div className="footer-brand">
          <span className="logo-icon">💜</span>
          <span className="logo-text">GlamWig</span>
          <p className="footer-tagline">Virtual Wig Try-On Experience</p>
        </div>
        <div className="footer-links">
          <div className="footer-column">
            <h4>Help</h4>
            <a href="#">FAQ</a>
            <a href="mailto:mimiswigginout@gmail.com">Contact Us</a>
          </div>
        </div>
        <div className="footer-bottom">
          <p>&copy; 2025 GlamWig. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};

// ============ Auth Pages ============
const LoginPage = () => {
  const { login } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await login(email, password);
      toast({ title: "Welcome back!" });
      navigate('/try-on');
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast({ 
        title: "Login failed", 
        description: typeof detail === 'string' ? detail : "Invalid email or password",
        variant: "destructive" 
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <Card className="auth-card">
        <CardHeader>
          <CardTitle>Welcome Back</CardTitle>
          <CardDescription>Sign in to continue your wig journey</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="auth-form">
            <div className="form-row">
              <Label htmlFor="email">Email</Label>
              <Input 
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                data-testid="login-email"
              />
            </div>
            <div className="form-row">
              <Label htmlFor="password">Password</Label>
              <Input 
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                data-testid="login-password"
              />
            </div>
            <Button 
              type="submit" 
              disabled={isLoading}
              className="btn-primary-glamwig auth-submit"
              data-testid="login-submit"
            >
              {isLoading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>
        </CardContent>
        <CardFooter className="auth-footer">
          <p>Don't have an account? <Link to="/register">Sign up free</Link></p>
        </CardFooter>
      </Card>
    </div>
  );
};

const RegisterPage = () => {
  const { register } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast({ title: "Password must be at least 6 characters", variant: "destructive" });
      return;
    }
    setIsLoading(true);
    try {
      await register(email, password, name);
      toast({ title: "Welcome to GlamWig!", description: "Your 15-day free trial has started!" });
      navigate('/try-on');
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast({ 
        title: "Registration failed", 
        description: typeof detail === 'string' ? detail : "Could not create account",
        variant: "destructive" 
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <Card className="auth-card">
        <CardHeader>
          <CardTitle>Create Your Account</CardTitle>
          <CardDescription>Start your 15-day FREE trial!</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="auth-form">
            <div className="form-row">
              <Label htmlFor="name">Name (optional)</Label>
              <Input 
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                data-testid="register-name"
              />
            </div>
            <div className="form-row">
              <Label htmlFor="email">Email</Label>
              <Input 
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                data-testid="register-email"
              />
            </div>
            <div className="form-row">
              <Label htmlFor="password">Password</Label>
              <Input 
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 6 characters"
                required
                data-testid="register-password"
              />
            </div>
            <Button 
              type="submit" 
              disabled={isLoading}
              className="btn-primary-glamwig auth-submit"
              data-testid="register-submit"
            >
              {isLoading ? 'Creating account...' : 'Sign Up Free'}
            </Button>
          </form>
          <div className="free-tries-info">
            <CheckIcon /> 15-day free trial included
          </div>
        </CardContent>
        <CardFooter className="auth-footer">
          <p>Already have an account? <Link to="/login">Sign in</Link></p>
        </CardFooter>
      </Card>
    </div>
  );
};

// ============ Landing Page ============
const LandingPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoadingSubscribe, setIsLoadingSubscribe] = useState(false);

  const testimonials = [
    { name: "Ruth J.", text: "Amazing experience! Found my perfect wig without leaving home.", rating: 5 },
    { name: "Verda T.", text: "The AI try-on is so realistic. Loved being able to see different styles on myself!", rating: 5 },
    { name: "Jan C.", text: "Great selection and the virtual try-on saved me so much time.", rating: 5 }
  ];

  const handleSubscribe = async () => {
    if (!user) {
      navigate('/register');
      return;
    }

    setIsLoadingSubscribe(true);
    try {
      const token = localStorage.getItem('glamwig_token');
      const response = await axios.post(`${API}/payments/create-subscription`, {
        lookup_key: "glamwig_pro_monthly",
        quantity: 1,
        origin_url: window.location.origin
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      toast({ 
        title: "Error", 
        description: "Could not start subscription. Please try again.",
        variant: "destructive" 
      });
    } finally {
      setIsLoadingSubscribe(false);
    }
  };

  return (
    <div className="landing-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <h1 className="hero-title">Find Your Perfect Wig</h1>
          <p className="hero-subtitle">
            Try on stunning wigs virtually using our AI-powered technology. 
            See exactly how you'll look before you buy.
          </p>
          <div className="hero-buttons">
            <Button 
              onClick={() => user ? navigate('/try-on') : navigate('/register')} 
              className="btn-primary-glamwig"
              data-testid="try-now-btn"
            >
              <SparklesIcon /> {user ? 'Try On Now' : 'Start Free'}
            </Button>
          </div>
        </div>
        <div className="hero-image">
          <div className="hero-image-placeholder">
            <span>✨</span>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="how-it-works">
        <h2 className="section-title">How It Works</h2>
        <div className="steps-grid">
          <div className="step-card">
            <div className="step-number">1</div>
            <div className="step-icon"><UploadIcon /></div>
            <h3>Upload Your Photo</h3>
            <p>Take a selfie or upload an existing photo</p>
          </div>
          <div className="step-card">
            <div className="step-number">2</div>
            <div className="step-icon">💇‍♀️</div>
            <h3>Choose a Wig</h3>
            <p>Browse our collection or upload your own</p>
          </div>
          <div className="step-card">
            <div className="step-number">3</div>
            <div className="step-icon"><SparklesIcon /></div>
            <h3>AI Try-On</h3>
            <p>See how the wig looks on you instantly</p>
          </div>
          <div className="step-card">
            <div className="step-number">4</div>
            <div className="step-icon"><HeartIcon /></div>
            <h3>Save & Share</h3>
            <p>Download, email, or add to cart</p>
          </div>
        </div>
      </section>

      {/* Subscription CTA */}
      <section className="subscription-section">
        <Card className="subscription-card">
          <CardHeader>
            <div className="subscription-badge"><CrownIcon /> GlamWig Pro</div>
            <CardTitle className="subscription-title">Unlimited Try-Ons</CardTitle>
            <CardDescription>Get unlimited access to all wig try-ons</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="subscription-price">
              <span className="price-amount">$9.99</span>
              <span className="price-period">/month</span>
            </div>
            <ul className="subscription-features">
              <li><CheckIcon /> Unlimited virtual try-ons</li>
              <li><CheckIcon /> Upload custom wigs</li>
              <li><CheckIcon /> Save and share results</li>
              <li><CheckIcon /> Priority support</li>
              <li><CheckIcon /> Cancel anytime</li>
            </ul>
          </CardContent>
          <CardFooter>
            <Button 
              onClick={handleSubscribe}
              disabled={isLoadingSubscribe || user?.is_subscribed}
              className="btn-primary-glamwig subscribe-btn"
              data-testid="subscribe-btn"
            >
              {user?.is_subscribed ? 'Already Subscribed' : isLoadingSubscribe ? 'Loading...' : 'Subscribe Now'}
            </Button>
            {!user?.is_subscribed && (
              <p className="trial-notice">Start your 15-day free trial. Cancel anytime before day 15 and you won't be charged.</p>
            )}
          </CardFooter>
        </Card>
        {!user && (
          <p className="subscription-note">
            New users get a <strong>15-day free trial</strong> - <Link to="/register">Sign up now</Link>
          </p>
        )}
      </section>

      {/* Testimonials */}
      <section className="testimonials">
        <h2 className="section-title">What Our Customers Say</h2>
        <div className="testimonials-grid">
          {testimonials.map((testimonial, index) => (
            <Card key={index} className="testimonial-card">
              <CardContent className="testimonial-content">
                <div className="stars">{"⭐".repeat(testimonial.rating)}</div>
                <p className="testimonial-text">"{testimonial.text}"</p>
                <p className="testimonial-name">— {testimonial.name}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>
    </div>
  );
};

// ============ Try-On Page ============
const TryOnPage = () => {
  const { user, usage, refreshUsage } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [wigs, setWigs] = useState([]);
  const [savedCustomWigs, setSavedCustomWigs] = useState([]);
  const [selectedWig, setSelectedWig] = useState(null);
  const [userImage, setUserImage] = useState(null);
  const [userImagePreview, setUserImagePreview] = useState(null);
  const [resultImage, setResultImage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSavingWig, setIsSavingWig] = useState(false);
  const [customWig, setCustomWig] = useState({
    name: '',
    style: '',
    color: '',
    image: null,
    imagePreview: null
  });
  const [showCustomForm, setShowCustomForm] = useState(false);
  const [emailAddress, setEmailAddress] = useState('');
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showSubscribeModal, setShowSubscribeModal] = useState(false);
  
  // Web Image Search state
  const [webSearchQuery, setWebSearchQuery] = useState('');
  const [webSearchResults, setWebSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);

  // Web Image Search function
  const handleWebSearch = async () => {
    if (!webSearchQuery.trim()) {
      toast({ title: "Please enter a search term", variant: "destructive" });
      return;
    }
    
    setIsSearching(true);
    setShowSearchResults(true);
    
    try {
      const response = await axios.post(`${API}/search-wig-images`, {
        query: webSearchQuery
      });
      
      if (response.data.success) {
        setWebSearchResults(response.data.images);
        if (response.data.images.length === 0) {
          toast({ title: "No images found", description: "Try a different search term" });
        }
      }
    } catch (error) {
      console.error("Error searching for images:", error);
      toast({ title: "Search failed", description: "Please try again", variant: "destructive" });
    } finally {
      setIsSearching(false);
    }
  };

  // Select image from web search results
  const selectWebImage = async (imageUrl, title) => {
    try {
      toast({ title: "Loading image...", description: "Please wait" });
      
      // Use backend proxy to avoid CORS issues
      const response = await axios.get(`${API}/proxy-image`, {
        params: { url: imageUrl }
      });
      
      if (response.data.success) {
        const imagePreview = response.data.data;
        setCustomWig(prev => ({
          ...prev,
          name: title || 'Web Wig',
          imagePreview: imagePreview
        }));
        
        // Automatically select this wig
        setSelectedWig({
          id: 'web_' + Date.now(),
          name: title || 'Web Wig',
          style: 'Custom',
          color: 'Custom',
          price: 0,
          is_custom: true,
          image_url: imagePreview
        });
        
        setShowSearchResults(false);
        setWebSearchQuery('');
        setWebSearchResults([]);
        toast({ title: "Wig selected!", description: "Now upload your photo and hit Generate!" });
      }
    } catch (error) {
      console.error("Error loading image:", error);
      toast({ title: "Failed to load image", description: "Please try another one", variant: "destructive" });
    }
  };

  const fetchSavedCustomWigs = async () => {
    try {
      const sessionId = getSessionId();
      const response = await axios.get(`${API}/custom-wigs?session_id=${sessionId}`);
      setSavedCustomWigs(response.data);
    } catch (error) {
      console.error("Error fetching saved wigs:", error);
    }
  };

  const handleSaveCustomWig = async () => {
    if (!customWig.name || !customWig.style || !customWig.color || !customWig.imagePreview) {
      toast({ title: "Please fill all fields and upload an image", variant: "destructive" });
      return;
    }

    setIsSavingWig(true);
    try {
      const sessionId = getSessionId();
      const response = await axios.post(`${API}/custom-wigs`, {
        name: customWig.name,
        style: customWig.style,
        color: customWig.color,
        image: customWig.imagePreview,
        session_id: sessionId
      });

      if (response.data.success) {
        toast({ title: "Wig saved!", description: "Your custom wig has been saved to your collection." });
        fetchSavedCustomWigs();
        setCustomWig({ name: '', style: '', color: '', image: null, imagePreview: null });
        setShowCustomForm(false);
      }
    } catch (error) {
      toast({ title: "Error saving wig", variant: "destructive" });
    } finally {
      setIsSavingWig(false);
    }
  };

  const handleDeleteCustomWig = async (wigId) => {
    try {
      await axios.delete(`${API}/custom-wigs/${wigId}`);
      toast({ title: "Wig deleted" });
      fetchSavedCustomWigs();
      if (selectedWig?.id === wigId) {
        setSelectedWig(null);
      }
    } catch (error) {
      toast({ title: "Error deleting wig", variant: "destructive" });
    }
  };

  useEffect(() => {
    const fetchWigs = async () => {
      try {
        const response = await axios.get(`${API}/wigs`);
        setWigs(response.data);
      } catch (error) {
        console.error("Error fetching wigs:", error);
      }
    };
    fetchWigs();
    fetchSavedCustomWigs();
  }, []);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUserImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setUserImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
      setResultImage(null);
    }
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      setUserImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setUserImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
      setResultImage(null);
    }
  }, []);

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleTryOn = async () => {
    if (!user) {
      navigate('/register');
      return;
    }

    if (!selectedWig) {
      toast({ title: "Please select a wig first", variant: "destructive" });
      return;
    }
    if (!userImage) {
      toast({ title: "Please upload your photo first", variant: "destructive" });
      return;
    }

    // Check if user can try on
    if (!usage?.can_try_on) {
      setShowSubscribeModal(true);
      return;
    }

    setIsLoading(true);
    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64Image = reader.result;
        const token = localStorage.getItem('glamwig_token');
        
        const requestData = {
          user_image: base64Image,
          wig_id: selectedWig.id,
          wig_name: selectedWig.name,
          wig_style: selectedWig.style,
          wig_color: selectedWig.color,
          custom_wig_image: selectedWig.is_custom && selectedWig.image_url ? selectedWig.image_url : null
        };
        
        const response = await axios.post(`${API}/try-on`, requestData, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.data.success) {
          setResultImage(response.data.result_image);
          toast({ title: "Try-on complete!", description: "See your new look below!" });
          refreshUsage();
        }
        setIsLoading(false);
      };
      reader.readAsDataURL(userImage);
    } catch (error) {
      console.error("Error generating try-on:", error);
      const detail = error.response?.data?.detail;
      
      if (error.response?.status === 403) {
        setShowSubscribeModal(true);
      } else if (error.response?.status === 401) {
        navigate('/register');
      } else {
        toast({ 
          title: "Error generating try-on", 
          description: typeof detail === 'string' ? detail : "Please try again",
          variant: "destructive" 
        });
      }
      setIsLoading(false);
    }
  };

  const handleSubscribe = async () => {
    try {
      const token = localStorage.getItem('glamwig_token');
      const response = await axios.post(`${API}/payments/create-subscription`, {
        lookup_key: "glamwig_pro_monthly",
        quantity: 1,
        origin_url: window.location.origin
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      toast({ 
        title: "Error", 
        description: "Could not start subscription. Please try again.",
        variant: "destructive" 
      });
    }
  };

  const handleManageSubscription = async () => {
    try {
      const token = localStorage.getItem('glamwig_token');
      const response = await axios.post(`${API}/payments/create-portal-session`, {
        origin_url: window.location.origin
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.portal_url) {
        window.location.href = response.data.portal_url;
      }
    } catch (error) {
      toast({ 
        title: "Error", 
        description: "Could not open subscription management. Please try again.",
        variant: "destructive" 
      });
    }
  };

  const handleDownload = () => {
    if (resultImage) {
      const link = document.createElement('a');
      link.href = resultImage;
      link.download = `glamwig-tryon-${selectedWig?.name || 'result'}.png`;
      link.click();
      toast({ title: "Image downloaded!" });
    }
  };

  const handleShare = async () => {
    if (resultImage && navigator.share) {
      try {
        await navigator.share({
          title: 'My GlamWig Try-On',
          text: `Check out how I look with the ${selectedWig?.name}!`,
          url: window.location.href
        });
      } catch (error) {
        console.log('Share cancelled');
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast({ title: "Link copied to clipboard!" });
    }
  };

  const handleEmailResult = async () => {
    if (!emailAddress) {
      toast({ title: "Please enter an email address", variant: "destructive" });
      return;
    }
    
    try {
      const formData = new FormData();
      formData.append('email', emailAddress);
      formData.append('wig_name', selectedWig?.name || 'Custom Wig');
      formData.append('result_image', resultImage);
      
      await axios.post(`${API}/email-result`, formData);
      toast({ title: "Email sent!", description: `Result sent to ${emailAddress}` });
      setShowEmailModal(false);
      setEmailAddress('');
    } catch (error) {
      toast({ title: "Error sending email", variant: "destructive" });
    }
  };

  const handleSaveResult = async () => {
    if (!resultImage) return;
    
    try {
      const formData = new FormData();
      formData.append('session_id', getSessionId());
      formData.append('wig_name', selectedWig?.name || 'Custom Wig');
      formData.append('result_image', resultImage);
      
      await axios.post(`${API}/save-result`, formData);
      toast({ title: "Result saved!", description: "You can access your saved results anytime" });
    } catch (error) {
      toast({ title: "Error saving result", variant: "destructive" });
    }
  };

  // Usage display component
  const UsageDisplay = () => {
    if (!user) return null;
    
    if (user.is_subscribed) {
      return (
        <div className="usage-display pro">
          <CrownIcon /> <span>Pro - Unlimited Try-Ons</span>
          <Button 
            size="sm" 
            variant="outline"
            onClick={handleManageSubscription}
            className="manage-sub-btn"
          >
            Manage Subscription
          </Button>
        </div>
      );
    }

    // Check if user is on free trial
    const isOnTrial = !user.is_subscribed && usage?.is_trial_active;
    const trialDaysRemaining = usage?.trial_days_remaining || 0;

    return (
      <div className="usage-display">
        {isOnTrial ? (
          <span className="trial-status">
            <SparklesIcon /> {trialDaysRemaining} days left in free trial
          </span>
        ) : (
          <span>Trial expired</span>
        )}
        {(!isOnTrial || trialDaysRemaining <= 3) && (
          <Button 
            size="sm" 
            onClick={() => setShowSubscribeModal(true)}
            className="btn-primary-glamwig upgrade-btn"
          >
            Upgrade to Pro
          </Button>
        )}
      </div>
    );
  };

  return (
    <div className="try-on-page">
      <div className="try-on-container">
        <h1 className="page-title">Virtual Wig Try-On</h1>
        <p className="page-subtitle">Upload your photo, choose a wig, and see the magic happen!</p>
        
        <UsageDisplay />

        <div className="try-on-grid">
          {/* Left Panel - Photo Upload */}
          <div className="upload-panel">
            <Card className="upload-card">
              <CardHeader>
                <CardTitle>Step 1: Upload Your Photo</CardTitle>
                <CardDescription>Take a clear front-facing photo for best results</CardDescription>
              </CardHeader>
              <CardContent>
                <div 
                  className="upload-zone"
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  data-testid="upload-zone"
                >
                  {userImagePreview ? (
                    <div className="preview-container">
                      <img src={userImagePreview} alt="Your photo" className="user-preview" />
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => { setUserImage(null); setUserImagePreview(null); setResultImage(null); }}
                        className="change-photo-btn"
                      >
                        Change Photo
                      </Button>
                    </div>
                  ) : (
                    <label className="upload-label">
                      <UploadIcon />
                      <span>Drag & drop or click to upload</span>
                      <input 
                        type="file" 
                        accept="image/*" 
                        onChange={handleImageUpload}
                        className="hidden-input"
                        data-testid="photo-upload-input"
                      />
                    </label>
                  )}
                </div>

                {/* Tips for Best Results */}
                <div className="tips-section">
                  <h4>Tips for Best Results</h4>
                  <ul className="tips-list">
                    <li><CheckIcon /> Use a clear, front-facing photo</li>
                    <li><CheckIcon /> Good lighting on your face</li>
                    <li><CheckIcon /> Hair pulled back or visible hairline</li>
                    <li><CheckIcon /> Neutral background preferred</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Middle Panel - Wig Selection */}
          <div className="selection-panel">
            <Card className="selection-card">
              <CardHeader>
                <CardTitle>Step 2: Choose a Wig</CardTitle>
                <CardDescription>Upload a wig photo or search the web for one</CardDescription>
              </CardHeader>
              <CardContent>
                {/* Web Search Section */}
                <div className="web-search-section" data-testid="web-search-section">
                  <Label>Search for Wig Images</Label>
                  <div className="web-search-input-row">
                    <Input
                      type="text"
                      placeholder="e.g., blonde bob, curly black, long red..."
                      value={webSearchQuery}
                      onChange={(e) => setWebSearchQuery(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleWebSearch()}
                      data-testid="web-search-input"
                    />
                    <Button 
                      onClick={handleWebSearch}
                      disabled={isSearching}
                      className="btn-primary-glamwig search-btn"
                      data-testid="web-search-btn"
                    >
                      {isSearching ? (
                        <span className="spinner-sm"></span>
                      ) : (
                        <SearchIcon />
                      )}
                      Search
                    </Button>
                  </div>
                  
                  {/* Search Results */}
                  {showSearchResults && (
                    <div className="web-search-results" data-testid="web-search-results">
                      {isSearching ? (
                        <div className="search-loading">
                          <span className="spinner"></span>
                          <p>Searching for wigs...</p>
                        </div>
                      ) : webSearchResults.length > 0 ? (
                        <>
                          <div className="search-results-header">
                            <span>{webSearchResults.length} results found</span>
                            <button 
                              className="close-results-btn"
                              onClick={() => { setShowSearchResults(false); setWebSearchResults([]); }}
                            >
                              ✕
                            </button>
                          </div>
                          <div className="search-results-grid">
                            {webSearchResults.map((img, index) => (
                              <button
                                key={index}
                                className="search-result-item"
                                onClick={() => selectWebImage(img.thumbnail || img.url, img.title)}
                                data-testid={`search-result-${index}`}
                              >
                                <img 
                                  src={img.thumbnail || img.url} 
                                  alt={img.title}
                                  onError={(e) => { e.target.style.display = 'none'; }}
                                />
                                <span className="result-title">{img.title?.slice(0, 30) || 'Wig'}</span>
                              </button>
                            ))}
                          </div>
                        </>
                      ) : (
                        <div className="no-results">
                          <p>No results found. Try a different search term.</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <Separator className="my-4" />
                
                <div className="or-divider">
                  <span>or upload your own</span>
                </div>

                {/* Custom Wig Upload - Always visible */}
                <div className="custom-wig-section custom-wig-section-main">
                  <div className="custom-wig-form">
                    <div className="form-row">
                      <Label>Upload Wig Image</Label>
                      <div className="custom-wig-upload-zone">
                        {customWig.imagePreview ? (
                          <div className="custom-wig-preview">
                            <img src={customWig.imagePreview} alt="Custom wig" />
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => {
                                setCustomWig(prev => ({...prev, image: null, imagePreview: null}));
                                // Deselect if this custom wig was selected
                                if (selectedWig?.is_custom) {
                                  setSelectedWig(null);
                                }
                              }}
                              className="remove-custom-image"
                            >
                              ✕
                            </Button>
                            {selectedWig?.is_custom && selectedWig?.image_url === customWig.imagePreview && (
                              <div className="custom-wig-selected-badge">Selected</div>
                            )}
                          </div>
                        ) : (
                          <label className="custom-wig-upload-label">
                            <UploadIcon />
                            <span>Click or drag to upload a wig photo</span>
                            <input 
                              type="file" 
                              accept="image/*" 
                              onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) {
                                  const reader = new FileReader();
                                  reader.onloadend = () => {
                                    const imagePreview = reader.result;
                                    setCustomWig(prev => ({
                                      ...prev,
                                      image: file,
                                      imagePreview: imagePreview
                                    }));
                                    // Automatically select this wig
                                    setSelectedWig({
                                      id: 'custom_' + Date.now(),
                                      name: 'Custom Wig',
                                      style: 'Custom',
                                      color: 'Custom',
                                      price: 0,
                                      is_custom: true,
                                      image_url: imagePreview
                                    });
                                    toast({ title: "Wig uploaded!", description: "Now upload your photo and hit Generate!" });
                                  };
                                  reader.readAsDataURL(file);
                                }
                              }}
                              className="hidden-input"
                              data-testid="custom-wig-image-input"
                            />
                          </label>
                        )}
                      </div>
                      <p className="wig-upload-tip">Tip: Use a wig photo with a plain/transparent background for best results</p>
                    </div>
                    
                    {/* Optional details - collapsed by default */}
                    {customWig.imagePreview && (
                      <div className="custom-wig-optional">
                        <p className="optional-label">Optional: Add a name for your records</p>
                        <div className="form-row">
                          <Input 
                            placeholder="Wig name (optional)"
                            value={customWig.name}
                            onChange={(e) => {
                              const newName = e.target.value;
                              setCustomWig({...customWig, name: newName});
                              if (selectedWig?.is_custom) {
                                setSelectedWig(prev => ({...prev, name: newName || 'Custom Wig'}));
                              }
                            }}
                            data-testid="custom-wig-name"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {selectedWig && (
                  <div className="selected-wig-info">
                    <Badge variant="secondary">Selected: {selectedWig.name}</Badge>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right Panel - Results */}
          <div className="results-panel">
            <Card className="results-card">
              <CardHeader>
                <CardTitle>Step 3: See Your New Look</CardTitle>
                <CardDescription>AI-powered virtual try-on</CardDescription>
              </CardHeader>
              <CardContent>
                <Button 
                  onClick={handleTryOn}
                  disabled={isLoading || !userImage || !selectedWig}
                  className="btn-primary-glamwig try-on-btn"
                  data-testid="generate-tryon-btn"
                >
                  {isLoading ? (
                    <>
                      <span className="spinner"></span>
                      Generating... (30-60s)
                    </>
                  ) : (
                    <>
                      <SparklesIcon /> Generate Try-On
                    </>
                  )}
                </Button>

                <div className="result-display">
                  {resultImage ? (
                    <div className="result-image-container">
                      {/* Stacked Comparison Layout */}
                      <div className="stacked-comparison" data-testid="stacked-comparison">
                        <div className="stacked-image-card original">
                          <span className="stacked-label">Original</span>
                          <img src={userImagePreview} alt="Original photo" />
                        </div>
                        <div className="stacked-image-card result">
                          <span className="stacked-label">With Wig</span>
                          <img src={resultImage} alt="Try-on result" data-testid="result-image" />
                        </div>
                      </div>
                      <div className="result-actions">
                        <Button onClick={handleDownload} className="btn-primary-glamwig download-result-btn" size="sm" data-testid="download-btn">
                          <DownloadIcon /> Download Result
                        </Button>
                      </div>
                      <div className="social-share-section">
                        <p className="share-label">Share your look:</p>
                        <div className="social-share-buttons">
                          <button 
                            className="social-btn facebook"
                            onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`, '_blank', 'width=600,height=400')}
                            aria-label="Share on Facebook"
                            data-testid="share-facebook"
                          >
                            <FacebookIcon />
                          </button>
                          <button 
                            className="social-btn instagram"
                            onClick={() => {
                              toast({ title: "Tip: Download the image first, then share it on Instagram!" });
                              handleDownload();
                            }}
                            aria-label="Share on Instagram"
                            data-testid="share-instagram"
                          >
                            <InstagramIcon />
                          </button>
                          <button 
                            className="social-btn pinterest"
                            onClick={() => window.open(`https://pinterest.com/pin/create/button/?url=${encodeURIComponent(window.location.href)}&description=${encodeURIComponent(`Check out my new look with ${selectedWig?.name || 'this wig'}!`)}`, '_blank', 'width=600,height=400')}
                            aria-label="Share on Pinterest"
                            data-testid="share-pinterest"
                          >
                            <PinterestIcon />
                          </button>
                          <button 
                            className="social-btn email"
                            onClick={() => setShowEmailModal(true)}
                            aria-label="Share via Email"
                            data-testid="share-email"
                          >
                            <MailIcon />
                          </button>
                        </div>
                      </div>
                      <Button onClick={handleSaveResult} variant="outline" className="save-to-history-btn" data-testid="save-btn">
                        <HeartIcon /> Save to My Results
                      </Button>
                      
                      {/* Try Another Wig - Upload new */}
                      <div className="try-another-section">
                        <Button 
                          variant="outline"
                          onClick={() => {
                            setResultImage(null);
                            setCustomWig({ name: '', style: '', color: '', image: null, imagePreview: null });
                            setSelectedWig(null);
                          }}
                          className="try-another-btn"
                          data-testid="try-another-btn"
                        >
                          <SparklesIcon /> Try Another Wig
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="result-placeholder">
                      <SparklesIcon />
                      <p>Your try-on result will appear here</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Email Modal */}
        {showEmailModal && (
          <div className="modal-overlay" onClick={() => setShowEmailModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h3>Email Your Result</h3>
              <Input 
                type="email"
                placeholder="your@email.com"
                value={emailAddress}
                onChange={(e) => setEmailAddress(e.target.value)}
              />
              <div className="modal-actions">
                <Button variant="outline" onClick={() => setShowEmailModal(false)}>Cancel</Button>
                <Button onClick={handleEmailResult} className="btn-primary-glamwig">Send</Button>
              </div>
            </div>
          </div>
        )}

        {/* Subscribe Modal */}
        {showSubscribeModal && (
          <div className="modal-overlay" onClick={() => setShowSubscribeModal(false)}>
            <div className="modal-content subscribe-modal" onClick={(e) => e.stopPropagation()}>
              <div className="subscribe-modal-header">
                <CrownIcon />
                <h3>Upgrade to GlamWig Pro</h3>
              </div>
              <p>Your free trial has ended. Subscribe for unlimited access!</p>
              <div className="subscribe-modal-price">
                <span className="price-amount">$9.99</span>
                <span className="price-period">/month</span>
              </div>
              <ul className="subscribe-modal-features">
                <li><CheckIcon /> Unlimited virtual try-ons</li>
                <li><CheckIcon /> Cancel anytime</li>
              </ul>
              <div className="modal-actions">
                <Button variant="outline" onClick={() => setShowSubscribeModal(false)}>Maybe Later</Button>
                <Button onClick={handleSubscribe} className="btn-primary-glamwig">Subscribe Now</Button>
              </div>
              <p className="trial-notice">Start your 15-day free trial. Cancel anytime before day 15 and you won't be charged.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ============ Payment Success/Cancel Pages ============
const PaymentSuccessPage = () => {
  const { refreshUsage } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking');
  const { search } = window.location;
  const sessionId = new URLSearchParams(search).get('session_id');

  useEffect(() => {
    const checkPayment = async () => {
      if (!sessionId) {
        setStatus('error');
        return;
      }

      try {
        const response = await axios.get(`${API}/payments/status/${sessionId}`);
        if (response.data.payment_status === 'paid') {
          setStatus('success');
          refreshUsage();
        } else {
          setStatus('pending');
          // Poll again after 2 seconds
          setTimeout(checkPayment, 2000);
        }
      } catch (error) {
        setStatus('error');
      }
    };

    checkPayment();
  }, [sessionId, refreshUsage]);

  return (
    <div className="payment-result-page">
      <Card className="payment-result-card">
        <CardContent className="payment-result-content">
          {status === 'checking' && (
            <>
              <div className="spinner large"></div>
              <h2>Verifying your payment...</h2>
            </>
          )}
          {status === 'pending' && (
            <>
              <div className="spinner large"></div>
              <h2>Processing your subscription...</h2>
              <p>This may take a moment.</p>
            </>
          )}
          {status === 'success' && (
            <>
              <div className="success-icon">✓</div>
              <h2>Welcome to GlamWig Pro!</h2>
              <p>Your subscription is now active. Enjoy unlimited try-ons!</p>
              <Button onClick={() => navigate('/try-on')} className="btn-primary-glamwig">
                Start Trying On Wigs
              </Button>
            </>
          )}
          {status === 'error' && (
            <>
              <div className="error-icon">✕</div>
              <h2>Something went wrong</h2>
              <p>We couldn't verify your payment. Please contact support.</p>
              <Button onClick={() => navigate('/')} className="btn-primary-glamwig">
                Go Home
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const PaymentCancelPage = () => {
  const navigate = useNavigate();

  return (
    <div className="payment-result-page">
      <Card className="payment-result-card">
        <CardContent className="payment-result-content">
          <h2>Payment Cancelled</h2>
          <p>No worries! You can still use your free trial.</p>
          <div className="payment-cancel-actions">
            <Button onClick={() => navigate('/try-on')} className="btn-primary-glamwig">
              Continue with Free Tries
            </Button>
            <Button onClick={() => navigate('/')} variant="outline">
              Go Home
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ============ Cart Page ============
const CartPage = ({ onCartUpdate }) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [cart, setCart] = useState({ items: [] });
  const [isLoading, setIsLoading] = useState(true);

  const fetchCart = async () => {
    try {
      const sessionId = getSessionId();
      const response = await axios.get(`${API}/cart/${sessionId}`);
      setCart(response.data);
    } catch (error) {
      console.error("Error fetching cart:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCart();
  }, []);

  const handleRemoveItem = async (itemId) => {
    try {
      const sessionId = getSessionId();
      await axios.delete(`${API}/cart/${sessionId}/item/${itemId}`);
      await fetchCart();
      if (onCartUpdate) onCartUpdate();
      toast({ title: "Item removed from cart" });
    } catch (error) {
      toast({ title: "Error removing item", variant: "destructive" });
    }
  };

  const handleUpdateQuantity = async (itemId, quantity) => {
    if (quantity < 1) return;
    try {
      const sessionId = getSessionId();
      await axios.put(`${API}/cart/${sessionId}/item/${itemId}?quantity=${quantity}`);
      await fetchCart();
      if (onCartUpdate) onCartUpdate();
    } catch (error) {
      toast({ title: "Error updating quantity", variant: "destructive" });
    }
  };

  const total = cart.items.reduce((sum, item) => sum + (item.price * item.quantity), 0);

  if (isLoading) {
    return (
      <div className="cart-page">
        <div className="cart-container">
          <div className="loading-spinner">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="cart-page">
      <div className="cart-container">
        <h1 className="page-title">Shopping Cart</h1>
        
        {cart.items.length === 0 ? (
          <Card className="empty-cart-card">
            <CardContent className="empty-cart-content">
              <ShoppingBagIcon />
              <h3>Your cart is empty</h3>
              <p>Try on some wigs and add them to your cart!</p>
              <Button onClick={() => navigate('/try-on')} className="btn-primary-glamwig">
                Start Shopping
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="cart-grid">
            <div className="cart-items">
              {cart.items.map((item) => (
                <Card key={item.id} className="cart-item-card" data-testid={`cart-item-${item.id}`}>
                  <CardContent className="cart-item-content">
                    <div className="cart-item-image">
                      {item.try_on_image ? (
                        <img src={item.try_on_image} alt={item.wig_name} />
                      ) : (
                        <div className="cart-item-placeholder">💇‍♀️</div>
                      )}
                    </div>
                    <div className="cart-item-details">
                      <h3>{item.wig_name}</h3>
                      <p>{item.wig_style} • {item.wig_color}</p>
                      <p className="cart-item-price">${item.price.toFixed(2)}</p>
                    </div>
                    <div className="cart-item-quantity">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleUpdateQuantity(item.id, item.quantity - 1)}
                      >
                        -
                      </Button>
                      <span>{item.quantity}</span>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleUpdateQuantity(item.id, item.quantity + 1)}
                      >
                        +
                      </Button>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleRemoveItem(item.id)}
                      className="remove-btn"
                    >
                      <TrashIcon />
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
            
            <Card className="cart-summary">
              <CardHeader>
                <CardTitle>Order Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="summary-row">
                  <span>Subtotal</span>
                  <span>${total.toFixed(2)}</span>
                </div>
                <div className="summary-row">
                  <span>Shipping</span>
                  <span>Free</span>
                </div>
                <Separator className="my-4" />
                <div className="summary-row total">
                  <span>Total</span>
                  <span>${total.toFixed(2)}</span>
                </div>
                <Button 
                  onClick={() => navigate('/checkout')}
                  className="btn-primary-glamwig checkout-btn"
                  data-testid="checkout-btn"
                >
                  Proceed to Checkout
                </Button>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

// ============ Checkout Page ============
const CheckoutPage = ({ onCartUpdate }) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [cart, setCart] = useState({ items: [] });
  const [isLoading, setIsLoading] = useState(false);
  const [customerInfo, setCustomerInfo] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip_code: ''
  });

  useEffect(() => {
    const fetchCart = async () => {
      try {
        const sessionId = getSessionId();
        const response = await axios.get(`${API}/cart/${sessionId}`);
        setCart(response.data);
        if (response.data.items.length === 0) {
          navigate('/cart');
        }
      } catch (error) {
        console.error("Error fetching cart:", error);
      }
    };
    fetchCart();
  }, [navigate]);

  const total = cart.items.reduce((sum, item) => sum + (item.price * item.quantity), 0);

  const handleInputChange = (e) => {
    setCustomerInfo({ ...customerInfo, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const requiredFields = ['first_name', 'last_name', 'email', 'phone', 'address', 'city', 'state', 'zip_code'];
    for (const field of requiredFields) {
      if (!customerInfo[field]) {
        toast({ title: `Please fill in ${field.replace('_', ' ')}`, variant: "destructive" });
        return;
      }
    }

    setIsLoading(true);
    try {
      const sessionId = getSessionId();
      const response = await axios.post(`${API}/checkout/${sessionId}`, customerInfo);
      
      if (response.data.success) {
        if (onCartUpdate) onCartUpdate();
        navigate(`/order-confirmation/${response.data.order_id}`);
      }
    } catch (error) {
      toast({ 
        title: "Checkout failed", 
        description: error.response?.data?.detail || "Please try again",
        variant: "destructive" 
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="checkout-page">
      <div className="checkout-container">
        <h1 className="page-title">Checkout</h1>
        
        <div className="checkout-grid">
          <form onSubmit={handleSubmit} className="checkout-form">
            <Card>
              <CardHeader>
                <CardTitle>Customer Information</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="form-grid">
                  <div className="form-row">
                    <Label htmlFor="first_name">First Name</Label>
                    <Input 
                      id="first_name"
                      name="first_name"
                      value={customerInfo.first_name}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="form-row">
                    <Label htmlFor="last_name">Last Name</Label>
                    <Input 
                      id="last_name"
                      name="last_name"
                      value={customerInfo.last_name}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="form-row full-width">
                    <Label htmlFor="email">Email</Label>
                    <Input 
                      id="email"
                      name="email"
                      type="email"
                      value={customerInfo.email}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="form-row full-width">
                    <Label htmlFor="phone">Phone</Label>
                    <Input 
                      id="phone"
                      name="phone"
                      type="tel"
                      value={customerInfo.phone}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="form-row full-width">
                    <Label htmlFor="address">Address</Label>
                    <Input 
                      id="address"
                      name="address"
                      value={customerInfo.address}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="form-row">
                    <Label htmlFor="city">City</Label>
                    <Input 
                      id="city"
                      name="city"
                      value={customerInfo.city}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="form-row">
                    <Label htmlFor="state">State</Label>
                    <Input 
                      id="state"
                      name="state"
                      value={customerInfo.state}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="form-row">
                    <Label htmlFor="zip_code">ZIP Code</Label>
                    <Input 
                      id="zip_code"
                      name="zip_code"
                      value={customerInfo.zip_code}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="mock-payment-card">
              <CardHeader>
                <CardTitle>Payment (Mock)</CardTitle>
                <CardDescription>This is a demo checkout - no real payment will be processed</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="mock-payment-info">
                  <Badge variant="secondary">🔒 Secure Mock Checkout</Badge>
                  <p>Click "Place Order" to complete your mock purchase</p>
                </div>
              </CardContent>
            </Card>

            <Button 
              type="submit"
              disabled={isLoading}
              className="btn-primary-glamwig place-order-btn"
              data-testid="place-order-btn"
            >
              {isLoading ? 'Processing...' : `Place Order - $${total.toFixed(2)}`}
            </Button>
          </form>

          <Card className="order-summary-card">
            <CardHeader>
              <CardTitle>Order Summary</CardTitle>
            </CardHeader>
            <CardContent>
              {cart.items.map((item) => (
                <div key={item.id} className="summary-item">
                  <span>{item.wig_name} x {item.quantity}</span>
                  <span>${(item.price * item.quantity).toFixed(2)}</span>
                </div>
              ))}
              <Separator className="my-4" />
              <div className="summary-row total">
                <span>Total</span>
                <span>${total.toFixed(2)}</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

// ============ Order Confirmation Page ============
const OrderConfirmationPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const response = await axios.get(`${API}/orders/${orderId}`);
        setOrder(response.data);
      } catch (error) {
        console.error("Error fetching order:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchOrder();
  }, [orderId]);

  if (isLoading) {
    return (
      <div className="confirmation-page">
        <div className="confirmation-container">
          <div className="loading-spinner">Loading...</div>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="confirmation-page">
        <div className="confirmation-container">
          <Card className="error-card">
            <CardContent>
              <h2>Order not found</h2>
              <Button onClick={() => navigate('/')} className="btn-primary-glamwig">
                Go Home
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="confirmation-page">
      <div className="confirmation-container">
        <Card className="confirmation-card">
          <CardContent className="confirmation-content">
            <div className="success-icon">✓</div>
            <h1>Order Confirmed!</h1>
            <p className="order-number">Order #{order.id.slice(0, 8).toUpperCase()}</p>
            <p className="mock-notice">
              <Badge variant="secondary">Mock Order</Badge>
              This is a demo - no real payment was processed
            </p>
            
            <Separator className="my-6" />
            
            <div className="order-details">
              <h3>Order Details</h3>
              {order.items.map((item, index) => (
                <div key={index} className="order-item">
                  <span>{item.wig_name}</span>
                  <span>${item.price.toFixed(2)} x {item.quantity}</span>
                </div>
              ))}
              <Separator className="my-4" />
              <div className="order-total">
                <span>Total</span>
                <span>${order.total.toFixed(2)}</span>
              </div>
            </div>
            
            <div className="shipping-details">
              <h3>Shipping To</h3>
              <p>{order.customer.first_name} {order.customer.last_name}</p>
              <p>{order.customer.address}</p>
              <p>{order.customer.city}, {order.customer.state} {order.customer.zip_code}</p>
            </div>

            <div className="confirmation-actions">
              <Button onClick={() => navigate('/try-on')} className="btn-primary-glamwig">
                Try On More Wigs
              </Button>
              <Button onClick={() => navigate('/')} variant="outline">
                Back to Home
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// ============ Result History Page ============
const ResultHistoryPage = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedResult, setSelectedResult] = useState(null);
  const [compareMode, setCompareMode] = useState(false);
  const [compareSelection, setCompareSelection] = useState([]);
  const [isExporting, setIsExporting] = useState(false);
  const compareViewRef = useRef(null);

  const fetchResults = async () => {
    try {
      const token = localStorage.getItem('glamwig_token');
      const response = await axios.get(`${API}/my-results`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResults(response.data);
    } catch (error) {
      console.error("Error fetching results:", error);
      if (error.response?.status === 401) {
        navigate('/login');
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchResults();
  }, [user, navigate]);

  const handleDelete = async (resultId) => {
    try {
      const token = localStorage.getItem('glamwig_token');
      await axios.delete(`${API}/my-results/${resultId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: "Result deleted" });
      setResults(results.filter(r => r.id !== resultId));
      if (selectedResult?.id === resultId) {
        setSelectedResult(null);
      }
      setCompareSelection(compareSelection.filter(r => r.id !== resultId));
    } catch (error) {
      toast({ title: "Error deleting result", variant: "destructive" });
    }
  };

  const handleDownload = (result) => {
    const link = document.createElement('a');
    link.href = result.result_image;
    link.download = `glamwig-${result.wig_name || 'result'}.png`;
    link.click();
    toast({ title: "Image downloaded!" });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const toggleCompareSelection = (result) => {
    if (compareSelection.find(r => r.id === result.id)) {
      setCompareSelection(compareSelection.filter(r => r.id !== result.id));
    } else if (compareSelection.length < 4) {
      setCompareSelection([...compareSelection, result]);
    } else {
      toast({ title: "Maximum 4 images for comparison", variant: "destructive" });
    }
  };

  const isSelected = (resultId) => compareSelection.find(r => r.id === resultId);

  const exitCompareMode = () => {
    setCompareMode(false);
    setCompareSelection([]);
  };

  const exportComparison = async () => {
    if (!compareViewRef.current || compareSelection.length < 2) return;
    
    setIsExporting(true);
    toast({ title: "Preparing your comparison image..." });
    
    try {
      const canvas = await html2canvas(compareViewRef.current, {
        backgroundColor: '#fdfbf7',
        scale: 2,
        useCORS: true,
        allowTaint: true,
        logging: false
      });
      
      const link = document.createElement('a');
      link.download = `glamwig-comparison-${compareSelection.length}-looks.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      
      toast({ title: "Comparison downloaded!", description: "Your comparison image has been saved." });
    } catch (error) {
      console.error("Export error:", error);
      toast({ title: "Export failed", description: "Please try again.", variant: "destructive" });
    } finally {
      setIsExporting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="history-page">
        <div className="history-container">
          <div className="loading-spinner">Loading your results...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="history-page">
      <div className="history-container">
        <div className="history-header">
          <h1 className="page-title">My Try-On History</h1>
          <p className="page-subtitle">View all your saved wig try-on results</p>
          {results.length >= 2 && (
            <div className="compare-toggle">
              {compareMode ? (
                <Button variant="outline" onClick={exitCompareMode} data-testid="exit-compare-btn">
                  Exit Compare Mode
                </Button>
              ) : (
                <Button onClick={() => setCompareMode(true)} className="btn-primary-glamwig" data-testid="compare-mode-btn">
                  <SparklesIcon /> Compare Looks
                </Button>
              )}
            </div>
          )}
        </div>

        {compareMode && (
          <div className="compare-instructions">
            <p>Select 2-4 images to compare side by side</p>
            <span className="compare-count">{compareSelection.length}/4 selected</span>
          </div>
        )}

        {results.length === 0 ? (
          <Card className="empty-history-card">
            <CardContent className="empty-history-content">
              <HeartIcon />
              <h3>No saved results yet</h3>
              <p>Try on some wigs and save your favorites!</p>
              <Button onClick={() => navigate('/try-on')} className="btn-primary-glamwig">
                Start Trying On
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="history-grid">
            {results.map((result) => (
              <Card 
                key={result.id} 
                className={`history-card ${selectedResult?.id === result.id ? 'selected' : ''} ${compareMode && isSelected(result.id) ? 'compare-selected' : ''}`}
                onClick={() => compareMode ? toggleCompareSelection(result) : setSelectedResult(result)}
                data-testid={`history-card-${result.id}`}
              >
                {compareMode && (
                  <div className={`compare-checkbox ${isSelected(result.id) ? 'checked' : ''}`}>
                    {isSelected(result.id) && <CheckIcon />}
                  </div>
                )}
                <div className="history-card-image">
                  <img src={result.result_image} alt={result.wig_name} />
                </div>
                <CardContent className="history-card-content">
                  <h4>{result.wig_name || 'Custom Wig'}</h4>
                  <p className="history-date">{formatDate(result.created_at)}</p>
                  {!compareMode && (
                    <div className="history-card-actions">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); handleDownload(result); }}
                      >
                        <DownloadIcon />
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); handleDelete(result.id); }}
                        className="delete-btn"
                      >
                        <TrashIcon />
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Compare View */}
        {compareMode && compareSelection.length >= 2 && (
          <div className="compare-view-wrapper">
            <div className="compare-view" ref={compareViewRef} data-testid="compare-view">
              <div className="compare-view-header">
                <h3 className="compare-title">Side-by-Side Comparison</h3>
                <p className="compare-subtitle">GlamWig Try-On Results</p>
              </div>
              <div className={`compare-grid compare-grid-${compareSelection.length}`}>
                {compareSelection.map((result) => (
                  <div key={result.id} className="compare-item">
                    <img src={result.result_image} alt={result.wig_name} crossOrigin="anonymous" />
                    <div className="compare-item-info">
                      <h4>{result.wig_name || 'Custom Wig'}</h4>
                      <p>{formatDate(result.created_at)}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="compare-view-footer">
                <span className="compare-watermark">💜 GlamWig</span>
              </div>
            </div>
            <div className="compare-actions">
              <Button 
                onClick={exportComparison}
                disabled={isExporting}
                className="btn-primary-glamwig export-comparison-btn"
                data-testid="export-comparison-btn"
              >
                {isExporting ? (
                  <>
                    <span className="spinner"></span>
                    Exporting...
                  </>
                ) : (
                  <>
                    <DownloadIcon /> Download Comparison
                  </>
                )}
              </Button>
              <p className="export-tip">Save all {compareSelection.length} looks as one image</p>
            </div>
            <div className="individual-downloads">
              <p>Or download individually:</p>
              <div className="individual-download-btns">
                {compareSelection.map((result) => (
                  <Button 
                    key={result.id}
                    variant="outline" 
                    size="sm"
                    onClick={() => handleDownload(result)}
                  >
                    <DownloadIcon /> {result.wig_name || 'Custom'}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Lightbox Modal */}
        {selectedResult && !compareMode && (
          <div className="modal-overlay" onClick={() => setSelectedResult(null)}>
            <div className="lightbox-modal" onClick={(e) => e.stopPropagation()}>
              <button className="lightbox-close" onClick={() => setSelectedResult(null)}>×</button>
              <img src={selectedResult.result_image} alt={selectedResult.wig_name} />
              <div className="lightbox-info">
                <h3>{selectedResult.wig_name || 'Custom Wig'}</h3>
                <p>{formatDate(selectedResult.created_at)}</p>
                <div className="lightbox-actions">
                  <Button onClick={() => handleDownload(selectedResult)} className="btn-primary-glamwig">
                    <DownloadIcon /> Download
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ============ Main App ============
function AppContent() {
  return (
    <div className="App glamwig-app">
      <Header />
      <main className="main-content">
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/try-on" element={<TryOnPage />} />
          <Route path="/history" element={<ResultHistoryPage />} />
          <Route path="/payment/success" element={<PaymentSuccessPage />} />
          <Route path="/payment/cancel" element={<PaymentCancelPage />} />
        </Routes>
      </main>
      <Footer />
      <Toaster />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
